import { assert, defined } from "@glideapps/ts-necessities";
import { insertArrayItems, removeArrayItem, reorder, replaceArrayItem } from "@glide/support";
import type { ScreenDescription, ComponentDescription } from "@glide/app-description";
import { getScreenComponents, isClassScreenDescription } from "@glide/common-core";
import { getSubComponents, getComponentAtIndexes } from "@glide/generator/dist/js/sub-components";
import type { ComponentIndexes } from "@glide/common-core/dist/js/component-indexes";

/**
 * @returns The updated screen description or the original if reordering couldn't be performed
 */
export function handleReorderComponent(
    screenDescription: ScreenDescription | undefined,
    from: ComponentIndexes,
    to: ComponentIndexes
): ScreenDescription | undefined {
    if (!isClassScreenDescription(screenDescription)) {
        return screenDescription;
    }

    let components = [...getScreenComponents(screenDescription)];

    function removeSubComponent(index: readonly [number, number]): ComponentDescription | undefined {
        const [fromComponent, fromParent] = getComponentAtIndexes(defined(screenDescription), index);
        if (fromComponent === undefined || fromParent === undefined) return undefined;

        const fromComponents = defined(getSubComponents(fromParent));
        const newFromComponents = fromComponents.filter(c => c !== fromComponent);
        const newFromParent = { ...fromParent, components: newFromComponents };

        components = replaceArrayItem(components, index[0], newFromParent);
        return fromComponent;
    }

    function addSubComponent(component: ComponentDescription, index: readonly [number, number]): boolean {
        const [, toParent] = getComponentAtIndexes(defined(screenDescription), index);
        if (toParent === undefined) return false;

        const toComponents = getSubComponents(toParent);

        // ##disallowPuttingContainersIntoContainers:
        // And fail if we want to put something into a non-container.
        if (getSubComponents(component) !== undefined || toComponents === undefined) {
            return false;
        }

        const newToComponents = insertArrayItems(toComponents, defined(index[1]), component);
        const newToParent = { ...toParent, components: newToComponents };

        components = replaceArrayItem(components, index[0], newToParent);

        return true;
    }

    if (from.length === 1 && to.length === 1) {
        components = reorder(components, from[0], to[0]);
    } else if (from.length === 1 && to.length === 2) {
        assert(from[0] !== to[0]);

        const [fromComponent] = getComponentAtIndexes(screenDescription, from);
        if (fromComponent === undefined) return screenDescription;

        if (!addSubComponent(fromComponent, to)) return screenDescription;
        components = removeArrayItem(components, from[0]);

        return { ...screenDescription, components };
    } else if (from.length === 2 && to.length === 1) {
        const fromComponent = removeSubComponent(from);
        if (fromComponent === undefined) return screenDescription;

        components = insertArrayItems(components, to[0], fromComponent);

        return { ...screenDescription, components };
    } else if (from.length === 2 && to.length === 2) {
        if (from[0] === to[0]) {
            const [, parent] = getComponentAtIndexes(screenDescription, from);
            if (parent === undefined) return screenDescription;

            let subComponents = getSubComponents(parent);
            if (subComponents === undefined) return screenDescription;
            subComponents = reorder(subComponents, from[1], to[1]);

            const newParent = { ...parent, components: subComponents };

            components = replaceArrayItem(components, from[0], newParent);
        } else {
            const fromComponent = removeSubComponent(from);
            if (fromComponent === undefined) return screenDescription;

            if (!addSubComponent(fromComponent, to)) return screenDescription;
        }
    } else {
        return screenDescription;
    }

    return { ...screenDescription, components };
}
